import {useEffect, useRef, useState} from 'react';
import cx from 'classnames';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {Accordion, Carousel, Text} from '#component/base';
import {FullscreenModalHeader} from '#component/partial';
import {
  EventType,
  PageViewEvent,
  useAnalytics,
  UserActionEvent,
} from '#component/provider/AnalyticsProvider';
import {AccordionRoot} from '#component/util';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import * as copies from '#constant/tallyExplorer';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {renderFormattedContent} from './components/PageContent/PageContent';
import {ReferenceItem} from './components/ReferenceItem/ReferenceItem';
import {referencesCarouselGap} from './components/ReferenceItem/ReferenceItem.css';

import * as css from './TallyExplorer.css';

export const TallyExplorerScreen = () => {
  const [openIndex, setOpenIndex] = useState('');
  const {query} = useRouter();
  const {context = 'actionplan'} = query;
  const {isMobile} = useViewportSize();
  const {track} = useAnalytics();
  const trackedPageViewRef = useRef<boolean>();
  const returnUrl = context === 'actionplan' ? '/actionplan' : '/lifestylesummary';
  const returnLabel = `Return to ${context === 'actionplan' ? 'Action Plan' : 'Lifestyle Summary'}`;
  const copy = copies[context as 'lifestylesummary' | 'actionplan'];

  const contentItems = renderFormattedContent(copy.contents);

  const referenceItems = (copy.references || []).map((reference, index) => {
    return (
      <ReferenceItem
        key={index}
        id={`ref-${index + 1}`}
        isCarouselItem={isMobile}
        authors={reference.authors}
        title={reference.title}
        url={reference.url}
      />
    );
  });

  useEffect(() => {
    if (copy.analyticsName && !trackedPageViewRef.current) {
      trackedPageViewRef.current = true;
      track<PageViewEvent>({
        evtName: EventType.PageView,
        pageName: copy.analyticsName,
      });
    }
  }, [track, copy]);

  return (
    <div className={css.root}>
      <div className={css.layout}>
        <FullscreenModalHeader
          className={sprinkles({w: 'full', pY: [24, 32, 40]})}
          closeButtonHref={returnUrl}
          closeButtonLabel={returnLabel}
        />
      </div>
      <div className={cx(css.scrollable, sprinkles({pT: [12, 32, 80]}))}>
        <div className={cx(css.layout, sprinkles({mB: [32, 64, 96]}))}>
          <div className={cx(css.columns)}>
            <div className={cx(css.column, sprinkles({gap: [24, 32, 40], mB: [48, 64, 0]}))}>
              <Text as="h2" variant="heading2" color="deepBurgundy">
                {copy.title}
              </Text>
              <Text as="p" variant="bodyLarge" color="doveGray">
                {copy.subTitle}
              </Text>
            </div>
            <div className={cx(css.column, sprinkles({gap: [24, 36, 48]}))}>
              {contentItems.length > 0 && (
                <div className={sprinkles({flex: 'flex', dir: 'col', gap: [56, 64, 64]})}>
                  {contentItems}
                </div>
              )}
              {copy.accordions.length > 0 && (
                <AccordionRoot
                  collapsible
                  onValueChange={(index: string) => {
                    setOpenIndex(index);

                    const accordion = copy.accordions[parseInt(index)];

                    if (accordion?.analyticsName) {
                      track<UserActionEvent>({
                        evtName: EventType.UserAction,
                        actionName: accordion.analyticsName,
                      });
                    }
                  }}
                  type="single">
                  {copy.accordions.map((accordion, index) => (
                    <Accordion
                      key={index}
                      trigger={
                        <Text
                          variant="bodyLarge"
                          color={openIndex === `${index}` ? 'burgundy' : 'black'}
                          textAlign="left">
                          {accordion.title}
                        </Text>
                      }
                      value={`${index}`}>
                      <div
                        className={sprinkles({
                          flex: 'flex',
                          dir: 'col',
                          gap: [24, 36, 48],
                          w: 'full',
                          pY: [24, 32, 40],
                        })}>
                        {renderFormattedContent(accordion.contents)}
                      </div>
                    </Accordion>
                  ))}
                </AccordionRoot>
              )}
            </div>
          </div>
        </div>
        {referenceItems.length > 0 && (
          <div className={sprinkles({bg: 'springWood', pY: [48, 64, 80]})}>
            <div className={css.layout}>
              <div className={css.columns}>
                <div className={cx(css.column, sprinkles({mB: [64, 48, 0]}))}>
                  <Text variant="label" color="orange">
                    References
                  </Text>
                  <Text as="h2" variant="heading2" color="deepBurgundy">
                    Learn more
                  </Text>
                </div>
                <div className={css.column}>
                  <div className={css.references}>
                    {isMobile ? (
                      <Carousel gap={referencesCarouselGap} widths={['100%']}>
                        {referenceItems}
                      </Carousel>
                    ) : (
                      referenceItems
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const TallyExplorer: NextPage = withRequireUserState(TallyExplorerScreen, {
  userState: 'authenticated',
});
